import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { z } from 'zod/v4'

export const PiiInformationStore = z.object(
  {
    ssn: z
      .string()
      .regex(/^\d{3}-\d{2}-\d{4}$/, { message: 'SSN must be in the format ###-##-####' }),
    dob: z
      .date()
      .refine(
        function isAdult(date) {
          const today = new Date()
          const eighteenYearsAgo = new Date(today.setFullYear(today.getFullYear() - 18))
          return date <= eighteenYearsAgo
        },
        { message: 'You must be 18 years or older to apply for a card.' },
      )
      .refine((date) => date.getFullYear() >= 1900, {
        message: 'Year of birth must be after 1900',
      }),
    totalAnnualIncome: z
      .string()
      .regex(/^\d+$/, { message: 'Total Annual Income must be a number' }),
    monthlyHousingPayment: z
      .string()
      .regex(/^\d+$/, { message: 'Monthly Housing Payment must be a number' }),
  },
  {
    message: 'Pii information store must be a valid object',
  },
)

export type PiiInformationStore = z.infer<typeof PiiInformationStore>

interface UsePiiInformationStore {
  userPiiInformation: PiiInformationStore | undefined
  setUserPiiInformation: (userPiiInformation: PiiInformationStore | undefined) => void
  clearUserPiiInformation: () => void
}

export const usePiiInformationStore = create(
  persist<UsePiiInformationStore>(
    (set) => ({
      userPiiInformation: undefined,
      setUserPiiInformation: (userPiiInformation: PiiInformationStore | undefined) =>
        set({ userPiiInformation }),
      clearUserPiiInformation: () => set({ userPiiInformation: undefined }),
    }),
    {
      name: 'user-pii-storage',
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
)

export default usePiiInformationStore
